import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc, getDoc, query, orderBy, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';
import DeviceOnlineBadge from './DeviceOnlineBadge';

const ROLES = [
  { key: 'admin', label: 'Admin' },
  { key: 'manager', label: 'Manager' },
  { key: 'cashier', label: 'Cashier' },
  { key: 'employee', label: 'Employee' },
];

const PosMaintenancePage = () => {
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [posList, setPosList] = useState([]);
  const [posSessions, setPosSessions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPos, setEditingPos] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedPos, setSelectedPos] = useState(null);
  const [startingCash, setStartingCash] = useState({
    1: 0, 5: 0, 10: 0, 20: 0, 50: 0, 100: 0
  });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [employeePin, setEmployeePin] = useState('');

  const [formData, setFormData] = useState({
    posName: '',
    locationId: '',
    cash: 0,
  });
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [openDropdownPosId, setOpenDropdownPosId] = useState(null);
  const dropdownRefs = useRef({});

  const navigate = useNavigate();
  const auth = getAuth();

  // Hash a PIN with SHA-256 using the provided salt (same algorithm as EmployeesPage)
  const hashPin = async (pin, salt) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(pin + salt);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideAny = false;
      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) clickedInsideAny = true;
      });
      if (!clickedInsideAny) setOpenDropdownPosId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();
      
      // Load locations
      const locSnapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      const locs = locSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setLocations(locs);
      
      // Get selected location from user doc (synced with navbar)
      const userDoc = await getDoc(doc(db, 'users', uid));
      const activeLocId = userDoc.exists() ? userDoc.data().selectedLocationId : null;
      const effectiveLocId = activeLocId || (locs[0]?.id || null);
      setSelectedLocationId(effectiveLocId);
      
      // Load POS devices — filter by selected location
      let posQuery = query(collection(db, 'users', uid, 'pos'), orderBy('posName'));
      if (effectiveLocId) {
        posQuery = query(collection(db, 'users', uid, 'pos'), where('locationId', '==', effectiveLocId), orderBy('posName'));
      }
      const posSnapshot = await getDocs(posQuery);
      const posDevices = posSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPosList(posDevices);
      
      // Load active sessions only for this location's POS devices
      const sessionsSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'posSessions'), where('status', '==', 'open'))
      );
      const sessions = {};
      const posIds = new Set(posDevices.map(p => p.id));
      sessionsSnapshot.docs.forEach(d => {
        if (posIds.has(d.data().posId)) {
          sessions[d.data().posId] = { id: d.id, ...d.data() };
        }
      });
      setPosSessions(sessions);

      // Load active employees for POS session start
      try {
        const empSnapshot = await getDocs(
          query(collection(db, 'users', uid, 'employees'), where('status', '==', 'active'), orderBy('name'))
        );
        // Only show employees who can start POS sessions (exclude 'employee' role)
        const sessionEligible = empSnapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => e.role !== 'employee');
        setEmployees(sessionEligible);
      } catch (empErr) {
        console.error('Error loading employees:', empErr);
        setEmployees([]);
      }
    } catch (e) {
      console.error('Error loading POS data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePos = async (e) => {
    e.preventDefault();
    if (!user) return;
    
    try {
      const db = getFirestore();
      const posRef = editingPos 
        ? doc(db, 'users', user.uid, 'pos', editingPos.id)
        : doc(collection(db, 'users', user.uid, 'pos'));
      
      await setDoc(posRef, {
        posName: formData.posName.trim(),
        locationId: formData.locationId,
        cash: parseFloat(formData.cash) || 0,
        updatedAt: new Date(),
        ...(editingPos ? {} : { createdAt: new Date() })
      }, { merge: true });
      
      setShowModal(false);
      setEditingPos(null);
      setFormData({ posName: '', locationId: '', cash: 0 });
      await loadData(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', editingPos ? 'POS updated' : 'POS created', 'success');
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to save POS', 'error');
    }
  };

  const handlePairDevice = async (pos) => {
    if (!user) return;
    
    // Step 1: Ask for device type
    const { value: deviceType } = await Swal.fire({
      title: 'Select Device Type',
      input: 'select',
      inputOptions: {
        'pos': 'POS Terminal (Counter)',
        'handheld-pos': 'Handheld POS (Mobile)',
      },
      inputPlaceholder: 'Select device type',
      showCancelButton: true,
      confirmButtonText: 'Next',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
    });
    if (!deviceType) return;

    // Step 2: Ask for activation code
    const { value: code } = await Swal.fire({
      title: 'Pair Physical Device',
      input: 'text',
      inputLabel: 'Enter the activation code displayed on the device',
      inputPlaceholder: 'e.g. ABC123',
      showCancelButton: true,
      confirmButtonText: 'Pair',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
    });
    if (!code) return;

    try {
      setLoading(true);
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'pairDeviceByActivationCode');
      const result = await callable({
        activationCode: code.trim().toUpperCase(),
        deviceType: deviceType,
        linkedDocId: pos.id,
      });
      if (result.data.success) {
        await loadData(user.uid);
        Swal.fire('Paired', 'Device paired successfully', 'success');
      }
    } catch (error) {
      console.error('Error pairing device:', error);
      Swal.fire('Error', error.message || 'Failed to pair device', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpairDevice = async (pos) => {
    if (!user) return;
    const result = await Swal.fire({
      title: 'Unpair Physical Device?',
      text: `This will release the physical device from "${pos.posName}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Unpair',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'unpairDevice');
      await callable({
        deviceType: 'pos',
        linkedDocId: pos.id,
      });
      await loadData(user.uid);
      Swal.fire('Unpaired', 'Physical device has been released', 'success');
    } catch (error) {
      console.error('Error unpairing device:', error);
      Swal.fire('Error', error.message || 'Failed to unpair device', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePos = async (pos) => {
    if (!user) return;
    const result = await Swal.fire({
      title: 'Delete POS?',
      text: `Are you sure you want to delete "${pos.posName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    
    if (result.isConfirmed) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'pos', pos.id));
        await loadData(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'POS has been deleted', 'success');
      } catch (e) {
        Swal.fire('Error', 'Failed to delete POS', 'error');
      }
    }
  };

  const openNewPos = () => {
    setEditingPos(null);
    setFormData({ posName: '', locationId: selectedLocationId || (locations.length > 0 ? locations[0].id : ''), cash: 0 });
    setShowModal(true);
  };

  const openEditPos = (pos) => {
    setEditingPos(pos);
    setFormData({
      posName: pos.posName || '',
      locationId: pos.locationId || '',
      cash: pos.cash || 0,
    });
    setShowModal(true);
  };

  const handleStartSelling = (pos) => {
    setSelectedPos(pos);
    setStartingCash({ 1: 0, 5: 0, 10: 0, 20: 0, 50: 0, 100: 0 });
    setSelectedEmployeeId('');
    setEmployeePin('');
    setShowStartModal(true);
  };

  const calculateStartingTotal = () => {
    return Object.entries(startingCash).reduce((sum, [denom, count]) => sum + (parseInt(denom) * parseInt(count || 0)), 0);
  };

  const handleConfirmStart = async () => {
    if (!user || !selectedPos) return;

    // If an employee is selected, verify their PIN (skip for 'employee' role)
    let employeeData = null;
    if (selectedEmployeeId) {
      const emp = employees.find(e => e.id === selectedEmployeeId);
      if (!emp) {
        Swal.fire('Error', 'Selected employee not found', 'error');
        return;
      }
      // Verify passkey if the employee has one (all roles including employee)
      if (emp.pinHash) {
        if (!employeePin || employeePin.length < 4) {
          Swal.fire('Error', 'Please enter the employee passkey (4-6 digits)', 'error');
          return;
        }
        const inputHash = await hashPin(employeePin, emp.pinSalt);
        if (inputHash !== emp.pinHash) {
          Swal.fire('Error', 'Incorrect passkey', 'error');
          return;
        }
      }
      employeeData = {
        employeeId: emp.id,
        employeeName: emp.name,
        employeeRole: emp.role,
      };
    }

    try {
      const db = getFirestore();
      const sessionRef = doc(collection(db, 'users', user.uid, 'posSessions'));
      const startingTotal = calculateStartingTotal();

      const sessionData = {
        posId: selectedPos.id,
        locationId: selectedPos.locationId,
        startTime: new Date(),
        startingCash: startingTotal,
        startingCashBreakdown: { ...startingCash },
        endingCash: 0,
        endingCashBreakdown: { 1: 0, 5: 0, 10: 0, 20: 0, 50: 0, 100: 0 },
        totalSales: 0,
        totalTransactions: 0,
        status: 'open',
        ...(employeeData || {}),
      };

      await setDoc(sessionRef, sessionData);

      // Set the POS device's cash field to the starting cash amount
      const posRef = doc(db, 'users', user.uid, 'pos', selectedPos.id);
      await updateDoc(posRef, {
        cash: startingTotal,
        updatedAt: new Date(),
      });

      setShowStartModal(false);
      setSelectedEmployeeId('');
      setEmployeePin('');
      navigate(`/admin/pos/${selectedPos.id}?session=${sessionRef.id}`);
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to start session', 'error');
    }
  };

  const handleContinueSelling = (pos) => {
    const session = posSessions[pos.id];
    if (session) {
      // Persist session and force screensaver so user must verify passkey on the POS
      sessionStorage.setItem('activePosSession', JSON.stringify({ posId: pos.id, sessionId: session.id }));
      sessionStorage.setItem('screenSaverActive', 'true');
      navigate(`/admin/pos/${pos.id}?session=${session.id}`);
    }
  };

  const handleUnregisterReader = async (pos) => {
    if (!user || !pos.stripeTerminalId) return;
    const result = await Swal.fire({
      title: 'Unregister Reader?',
      text: `Are you sure you want to unregister the Stripe reader from "${pos.posName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Unregister',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: 'Unregistering...',
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); }
        });
        const functions = getFunctions();
        const deleteStripeTerminalReaderFn = httpsCallable(functions, 'deleteStripeTerminalReader');
        await deleteStripeTerminalReaderFn({
          posId: pos.id,
          stripeTerminalId: pos.stripeTerminalId,
        });
        await loadData(user.uid);
        Swal.fire('Success', 'Stripe Reader unregistered successfully!', 'success');
      } catch (error) {
        console.error('Error unregistering reader:', error);
        Swal.fire('Error', error.message || 'Failed to unregister Stripe Reader', 'error');
      }
    }
  };

  const handlePairReader = async (pos) => {
    if (!user) return;
    const location = locations.find(l => l.id === pos.locationId);
    if (!location) {
      Swal.fire('Error', 'Location not found for this POS', 'error');
      return;
    }
    if (!location.stripeLocationId) {
      Swal.fire('Error', 'This location does not have a Stripe Terminal location linked. Please link it in the Locations page first.', 'error');
      return;
    }

    const { value: registrationCode } = await Swal.fire({
      title: 'Pair Stripe Reader',
      input: 'text',
      inputLabel: 'Enter the pairing code displayed on your BBPOS WisePOS E device',
      inputPlaceholder: 'e.g. P123-456-789',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Please enter the pairing code';
        }
      }
    });

    if (!registrationCode) return;

    try {
      Swal.fire({
        title: 'Pairing Reader...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      const functions = getFunctions();
      const createStripeTerminalReaderFn = httpsCallable(functions, 'createStripeTerminalReader');
      await createStripeTerminalReaderFn({
        posId: pos.id,
        locationId: pos.locationId,
        stripeLocationId: location.stripeLocationId,
        registrationCode: registrationCode.trim(),
        label: pos.posName || 'BBPOS WisePOS E',
      });
      await loadData(user.uid);
      Swal.fire('Success', 'Stripe Reader paired successfully!', 'success');
    } catch (error) {
      console.error('Error pairing reader:', error);
      Swal.fire('Error', error.message || 'Failed to pair Stripe Reader', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getLocationName = (id) => {
    const loc = locations.find(l => l.id === id);
    return loc ? loc.companyName || loc.locationName : 'Unknown';
  };

  if (loading) {
    return (
      <AdminLayout title="Point of Sales">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Point of Sales">
      <SetupGuard prerequisites={['location']}>
      <div className="pos-maintenance-page">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-md-6">
            <h4 className="font-weight-bold">Point of Sales</h4>
            <p className="text-muted">
              <i className="fas fa-store mr-1"></i>
              {selectedLocationId ? getLocationName(selectedLocationId) : 'All Locations'}
            </p>
          </div>
          <div className="col-md-6 text-right">
            <button
              className="btn btn-outline-primary mr-2"
              style={{ borderRadius: 8 }}
              onClick={() => navigate('/admin/pos-sessions')}
            >
              <i className="fas fa-history mr-2"></i>All Sessions
            </button>
            <button
              className="btn btn-primary"
              style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
              onClick={openNewPos}
            >
              <i className="fas fa-plus mr-2"></i>Add POS
            </button>
          </div>
        </div>

        {/* POS Cards */}
        {posList.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-cash-register fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No POS devices for this location</h4>
            <p className="text-muted">Add a POS terminal to start selling at {selectedLocationId ? getLocationName(selectedLocationId) : 'this location'}</p>
          </div>
        ) : (
          <div className="row">
            {posList.map((pos) => {
              const hasSession = posSessions[pos.id];
              return (
                <div key={pos.id} className="col-md-6 col-lg-4 mb-4">
                  <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="card-title font-weight-bold m-0">{pos.posName}</h5>
                          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-store mr-1"></i>
                            {getLocationName(pos.locationId)}
                          </p>
                        </div>
                        <div className="position-relative" ref={el => { if (el) dropdownRefs.current[pos.id] = el; }}>
                          <button
                            className="btn btn-sm btn-link text-muted"
                            onClick={(e) => { e.stopPropagation(); setOpenDropdownPosId(openDropdownPosId === pos.id ? null : pos.id); }}
                          >
                            <i className="fas fa-ellipsis-v"></i>
                          </button>
                          {openDropdownPosId === pos.id && (
                            <div
                              className="position-absolute"
                              style={{
                                right: 0,
                                top: '100%',
                                background: '#fff',
                                border: '1px solid rgba(0,0,0,0.15)',
                                borderRadius: 8,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                zIndex: 1000,
                                minWidth: 160,
                              }}
                            >
                              <button className="dropdown-item d-flex align-items-center" style={{ padding: '10px 16px', fontSize: '0.9rem', border: 'none', background: 'none', width: '100%', cursor: 'pointer' }} onClick={() => { setOpenDropdownPosId(null); navigate(`/admin/pos-sessions/${pos.id}`); }}>
                                <i className="fas fa-history mr-2 text-primary" style={{ width: 20 }}></i>Sessions
                              </button>
                              <button className="dropdown-item d-flex align-items-center" style={{ padding: '10px 16px', fontSize: '0.9rem', border: 'none', background: 'none', width: '100%', cursor: 'pointer' }} onClick={() => { setOpenDropdownPosId(null); openEditPos(pos); }}>
                                <i className="fas fa-edit mr-2 text-primary" style={{ width: 20 }}></i>Edit
                              </button>
                              <button className="dropdown-item d-flex align-items-center" style={{ padding: '10px 16px', fontSize: '0.9rem', border: 'none', background: 'none', width: '100%', cursor: 'pointer' }} onClick={() => { setOpenDropdownPosId(null); handlePairReader(pos); }}>
                                <i className="fas fa-credit-card mr-2 text-primary" style={{ width: 20 }}></i>{pos.stripeTerminalId ? 'Re-pair Stripe Reader' : 'Pair Stripe Reader'}
                              </button>
                              {pos.stripeTerminalId && (
                                <button className="dropdown-item d-flex align-items-center text-warning" style={{ padding: '10px 16px', fontSize: '0.9rem', border: 'none', background: 'none', width: '100%', cursor: 'pointer' }} onClick={() => { setOpenDropdownPosId(null); handleUnregisterReader(pos); }}>
                                  <i className="fas fa-unlink mr-2" style={{ width: 20 }}></i>Unregister Reader
                                </button>
                              )}
                              {!pos.fodyfeeDeviceId && (
                                <button className="dropdown-item d-flex align-items-center" style={{ padding: '10px 16px', fontSize: '0.9rem', border: 'none', background: 'none', width: '100%', cursor: 'pointer' }} onClick={() => { setOpenDropdownPosId(null); handlePairDevice(pos); }}>
                                  <i className="fas fa-mobile-alt mr-2 text-info" style={{ width: 20 }}></i>Pair Physical Device
                                </button>
                              )}
                              {pos.fodyfeeDeviceId && (
                                <button className="dropdown-item d-flex align-items-center text-danger" style={{ padding: '10px 16px', fontSize: '0.9rem', border: 'none', background: 'none', width: '100%', cursor: 'pointer' }} onClick={() => { setOpenDropdownPosId(null); handleUnpairDevice(pos); }}>
                                  <i className="fas fa-unlink mr-2" style={{ width: 20 }}></i>Unpair Physical Device
                                </button>
                              )}
                              <div style={{ borderTop: '1px solid #e9ecef' }}></div>
                              <button className="dropdown-item d-flex align-items-center text-danger" style={{ padding: '10px 16px', fontSize: '0.9rem', border: 'none', background: 'none', width: '100%', cursor: 'pointer' }} onClick={() => { setOpenDropdownPosId(null); handleDeletePos(pos); }}>
                                <i className="fas fa-trash mr-2" style={{ width: 20 }}></i>Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-3">
                        <div 
                          className="d-flex align-items-center justify-content-center mr-3"
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: hasSession ? '#e8f5e9' : '#fff3e0',
                          }}
                        >
                          <i className={`fas ${hasSession ? 'fa-play-circle text-success' : 'fa-power-off text-warning'}`} style={{ fontSize: 20 }}></i>
                        </div>
                        <div>
                          <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                            {hasSession ? 'Session Active' : 'Session Closed'}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                            Drawer: {formatCurrency(pos.cash)}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                            <i className={`fas fa-credit-card mr-1 ${pos.stripeTerminalId ? 'text-success' : 'text-muted'}`}></i>
                            {pos.stripeTerminalId ? 'Reader paired' : 'No reader paired'}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                            <i className={`fas fa-mobile-alt mr-1 ${pos.fodyfeeDeviceId ? 'text-success' : 'text-muted'}`}></i>
                            {pos.fodyfeeDeviceId ? 'Physical device paired' : 'No physical device paired'}
                          </div>
                          <div className="mt-1">
                            <DeviceOnlineBadge deviceId={pos.fodyfeeDeviceId} />
                          </div>
                        </div>
                      </div>

                      {!pos.fodyfeeDeviceId ? (
                        <button
                          className="btn btn-block"
                          style={{
                            backgroundColor: hasSession ? '#28a745' : '#ff6b35',
                            borderColor: hasSession ? '#28a745' : '#ff6b35',
                            color: 'white',
                            borderRadius: '12px',
                          }}
                          onClick={() => hasSession ? handleContinueSelling(pos) : handleStartSelling(pos)}
                        >
                          <i className={`fas ${hasSession ? 'fa-play mr-2' : 'fa-power-off mr-2'}`}></i>
                          {hasSession ? 'Continue Selling' : 'Start Selling'}
                        </button>
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: '#e3f2fd',
                            borderRadius: '12px',
                            padding: '10px 16px',
                            fontSize: '0.9rem',
                            color: '#1976d2',
                            fontWeight: 500,
                          }}
                        >
                          <i className="fas fa-mobile-alt mr-2"></i>
                          Managed by physical device
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit POS Modal */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">{editingPos ? 'Edit POS' : 'Add POS'}</h5>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSavePos}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>POS Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.posName}
                      onChange={(e) => setFormData({ ...formData, posName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Location *</label>
                    <select
                      className="form-control"
                      value={formData.locationId}
                      onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                      required
                    >
                      <option value="">Select Location</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.companyName || loc.locationName}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                    {editingPos ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Start Session Modal */}
      {showStartModal && selectedPos && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Start Session — {selectedPos.posName}</h5>
                <button type="button" className="close" onClick={() => setShowStartModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {/* Employee selection */}
                <div className="form-group mb-3">
                  <label className="font-weight-bold">Employee (optional)</label>
                  <select
                    className="form-control"
                    value={selectedEmployeeId}
                    onChange={e => { setSelectedEmployeeId(e.target.value); setEmployeePin(''); }}
                  >
                    <option value="">— Select employee —</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} ({ROLES.find(r => r.key === emp.role)?.label || emp.role})
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Select an employee to tag this session. Employees with a passkey will be asked to enter it.
                  </small>
                </div>

                {/* PIN entry if the selected employee has a passkey */}
                {selectedEmployeeId && employees.find(e => e.id === selectedEmployeeId)?.pinHash && (
                  <div className="form-group mb-3">
                    <label className="font-weight-bold">Employee Passkey <span className="text-danger">*</span></label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      className="form-control"
                      value={employeePin}
                      onChange={e => setEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 4-6 digit PIN"
                      autoFocus
                    />
                  </div>
                )}

                <hr className="my-3" />
                <p className="text-muted">Enter the starting cash balance by bill denomination:</p>
                <div className="row">
                  {[1, 5, 10, 20, 50, 100].map(denom => (
                    <div key={denom} className="col-6 mb-3">
                      <label className="font-weight-bold">${denom} bills</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">x</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          value={startingCash[denom]}
                          onChange={(e) => setStartingCash({ ...startingCash, [denom]: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="alert alert-light border mt-3" style={{ borderRadius: '12px' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold">Total Starting Cash:</span>
                    <span className="font-weight-bold" style={{ color: '#ff6b35', fontSize: '1.25rem' }}>
                      {formatCurrency(calculateStartingTotal())}
                    </span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowStartModal(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                  onClick={handleConfirmStart}
                >
                  Start Selling
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </SetupGuard>
    </AdminLayout>
  );
};

export default PosMaintenancePage;
